import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { HostedFrame, IllusDressing_1 } from '@sg/garnish';
import type { OrderType } from '@sg/graphql-schema';

import { LoadingAnimation } from '../../components';
import { useMomoSurveyMessages } from './hooks';
import { LandingContent } from './RateOrderScreen.landing';

// ─────────────────────────────────────────────────────────────────────────────

export const MomosSurveyContent = (props: MomosSurveyContentProps) => {
  const { url, orderId, orderType } = props;

  const { formatMessage } = useIntl();

  // ─── State ─────────────────────────────────────────────────────────────────

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const { onMessage } = useMomoSurveyMessages({ url, orderId, orderType });

  const handleLoadEnd = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  // ───────────────────────────────────────────────────────────────────────────

  if (hasError) {
    return (
      <LandingContent>
        <LandingContent.Illustration source={IllusDressing_1} style={styles.errorIllustration} />

        <LandingContent.Title>{formatMessage(messages.errorTitle)}</LandingContent.Title>

        <LandingContent.Subtitle>{formatMessage(messages.errorSubtitle)}</LandingContent.Subtitle>
      </LandingContent>
    );
  }

  return (
    <View style={styles.container}>
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <LoadingAnimation />
        </View>
      )}

      <HostedFrame
        frameId="momos-rate-order-survey"
        frameHeight="100%"
        containerStyle={styles.frameContainer}
        source={{ uri: url }}
        isLoading={isLoading}
        onLoadEnd={handleLoadEnd}
        onError={handleError}
        onMessage={onMessage}
        acceptUnmarkedMessages
        sharedCookiesEnabled
        thirdPartyCookiesEnabled
      />
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  errorTitle: {
    defaultMessage: `We couldn't load this survey.`,
    description: 'Rate | Order | Momos Survey | Error View | Title',
  },
  errorSubtitle: {
    defaultMessage: 'Please try again later.',
    description: 'Rate | Order | Momos Survey | Error View | Subtitle',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
  },
  frameContainer: {
    flex: 1,
    height: '100%',
    width: '100%',
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFill,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorIllustration: {
    width: 200,
    height: 200,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type MomosSurveyContentProps = Readonly<{
  url: string;
  orderId: string;
  orderType: OrderType;
}>;
